import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import ShareResultsDialog from './ShareResultsDialog';
import ShareIcon from '@mui/icons-material/Share';

// Add interface for the window object to include the Responsly script
declare global {
  interface Window {
    responslyWidgetLoaded?: boolean;
  }
}

interface JudgmentDisplayProps {
  isFinalized: boolean;
  awaitingJudgment: boolean;
  judgment: string;
  topic: string;
}

const JudgmentDisplay: React.FC<JudgmentDisplayProps> = ({
  isFinalized,
  awaitingJudgment,
  judgment,
  topic,
}) => {
  const [showShareDialog, setShowShareDialog] = useState(false);

  // Add useEffect to load the Responsly script
  useEffect(() => {
    if (isFinalized && !awaitingJudgment && judgment && !window.responslyWidgetLoaded) {
      const script = document.createElement('script');
      script.src = 'https://app.responsly.com/widget/responsly-widget.min.js';
      script.async = true;
      script.onload = () => {
        window.responslyWidgetLoaded = true;
      };
      document.body.appendChild(script);
    }
  }, [isFinalized, awaitingJudgment, judgment]);

  if (!isFinalized) return null;

  // Updated regex patterns to handle different winner announcement formats
  const winnerMatch = judgment ? judgment.match(/(?:Therefore, )?(\w+) wins(?: the argument)?/) : null;
  const scoreMatch = judgment ? judgment.match(/(\d{1,3}-\d{1,3})/) : null;
  const winner = winnerMatch ? winnerMatch[1] : '';
  const score = scoreMatch ? scoreMatch[1] : '';
  
  // Check if it's a tie (50-50 score)
  const isTie = score === '50-50';
  
  // Remove the winner/score announcement from the detailed judgment
  const detailedJudgment = judgment ? judgment.replace(/(?:Therefore, )?.*wins(?: the argument)?: \d{1,3}-\d{1,3}\./, '').trim() : '';

  return (
    <Box
      sx={{
        mt: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      {/* Show spinner only if judgment is not yet available */}
      {awaitingJudgment && !judgment && (
        <>
          <CircularProgress color="primary" size={50} />
          <Typography variant="h2" sx={{ mt: 2 }}>
            Awaiting Judgment...
          </Typography>
        </>
      )}

      {/* Only show winner card if we have a finalized judgment */}
      {isFinalized && !awaitingJudgment && judgment && (
        <Card
          sx={{
            mb: 3,
            maxWidth: 600,
            margin: '20px auto',
            background: 'rgba(255, 255, 255, 0.9)',
            boxShadow: '0px 4px 8px rgba(102, 37, 102, 0.2)',
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 1 }}>
            <Typography variant="h2" gutterBottom>
              {isTie ? "It's a tie!" : `${winner} Wins!`}
            </Typography>
            <Typography variant="h3" sx={{ mt: 2, minWidth: '150px' }}>
              {score}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ShareIcon />}
              onClick={() => setShowShareDialog(true)}
              sx={{ mt: 2 }}
            >
              Share Results
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Detailed Judgment Card */}
      {isFinalized && !awaitingJudgment && judgment && (
        <>
          <Card
            sx={{
              maxWidth: 600,
              margin: '20px auto',
              boxShadow: '0px 4px 8px rgba(102, 37, 102, 0.2)',
            }}
          >
            <CardContent>
              <Typography variant="h2" gutterBottom>
                Detailed Judgment
              </Typography>
              <Typography variant="body1">{detailedJudgment}</Typography>
            </CardContent>
          </Card>

          <Button
            variant="contained"
            color="primary"
            onClick={() => window.location.href = '/'}
            sx={{
              maxWidth: 600,
              margin: '20px auto',
              display: 'block'
            }}
          >
            Start New Argument
          </Button>
        </>
      )}

      {/* Responsly Feedback Widget */}
      {isFinalized && !awaitingJudgment && judgment && (
        <Box sx={{ mt: 3, width: '100%', display: 'flex', justifyContent: 'center' }}>
          <div dangerouslySetInnerHTML={{ 
            __html: '<responsly-widget undefined window="slidup" size="small" position="right" scrolling="true"></responsly-widget>' 
          }} />
        </Box>
      )}

      <ShareResultsDialog
        open={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        winner={winner}
        score={score}
        topic={topic}
      />
    </Box>
  );
};

export default JudgmentDisplay;
